import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, CircularProgress } from '@material-ui/core';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import { useTranslation } from 'react-i18next';
import { Title } from '../../elements';
import { notifications } from '../../utils';
import { ApiService } from '../../hooks';
import useAuthContext from '../../hooks/useAuthContext';
import { brand } from '../../theme/brand';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiService = new ApiService();
  const { state } = useAuthContext();

  const farmNumberFromStore = state.isLoggedIn ? state.farmNumber : '';

  const [email, setEmail] = React.useState('');
  const [emailError, setEmailError] = React.useState(false);
  const [farmNumber, setFarmNumber] = React.useState('');
  const [farmNumberError, setFarmNumberError] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const handleSubmit = async () => {
    const farmNumberToSubmit = farmNumberFromStore || farmNumber.trim();

    if (!farmNumberToSubmit) {
      setFarmNumberError(true);
      notifications.warning(t('f-forgot-password-invalid-farm-number'));
      return;
    }

    if (!EMAIL_REGEX.test(email.trim())) {
      setEmailError(true);
      notifications.warning(t('f-forgot-password-invalid-email'));
      return;
    }

    setFarmNumberError(false);
    setEmailError(false);
    setIsBusy(true);

    await apiService.api.auth
      .authForgotPassword({ email: email.trim(), farmNumber: farmNumberToSubmit })
      .then(() => {
        setSubmitted(true);
      })
      .catch(() => {
        notifications.error(t('f-forgot-password-error'));
      })
      .finally(() => setIsBusy(false));
  };

  return (
    <div className="flex justify-center py-10 px-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 bg-gray-50">
          <img src={brand.logoSrc} alt={brand.logoAlt} className="w-8 h-8 rounded-full" />
          <Title>{t('f-forgot-password-title')}</Title>
        </div>

        <div className="p-6">
          {submitted ? (
            <>
              <p className="text-sm text-gray-700 mb-4">{t('f-forgot-password-success')}</p>
              <Button variant="outlined" color="primary" fullWidth onClick={() => navigate('/login')}>
                {t('f-forgot-password-back-to-login')}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">{t('f-forgot-password-info')}</p>

              {!farmNumberFromStore && (
                <TextField
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  autoFocus
                  value={farmNumber}
                  onChange={(e) => {
                    setFarmNumber(e.target.value);
                    if (farmNumberError) setFarmNumberError(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSubmit();
                  }}
                  id="forgotPasswordFarmNumber"
                  label={t('f-forgot-password-farm-number')}
                  error={farmNumberError}
                  disabled={isBusy}
                />
              )}

              <TextField
                variant="outlined"
                type="email"
                margin="dense"
                fullWidth
                autoFocus={!!farmNumberFromStore}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit();
                }}
                id="forgotPasswordEmail"
                label={t('f-login-email')}
                error={emailError}
                disabled={isBusy}
              />

              <Button
                id="forgotPasswordSubmitBtn"
                startIcon={isBusy ? <CircularProgress size={16} /> : <LockOpenIcon />}
                variant="contained"
                color="primary"
                fullWidth
                disabled={isBusy}
                className="!mt-4"
                onClick={handleSubmit}
              >
                {t('f-forgot-password-submit')}
              </Button>

              <Button variant="text" color="primary" fullWidth className="!mt-2" onClick={() => navigate('/login')}>
                {t('f-forgot-password-back-to-login')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
